'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
    ComponentProps,
    KeyboardEvent,
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Controller, UseFormReturn, useForm, useWatch } from 'react-hook-form';
import { AddressIcon } from '@/features/address/ui/address-icon';
import { AddressIconOption } from '@/features/address/ui/address-icon/types';
import { ConfirmDeleteAddressModal } from '@/features/address/ui/confirm-delete-address-modal';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { AddressInput, UserAddressType, Address } from '@/shared/model/Address';
import { Button } from '@/shared/ui/button';
import { Dropdown } from '@/shared/ui/dropdown';
import { Form } from '@/shared/ui/form';
import { Icon } from '@/shared/ui/icon';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import { Popover } from '@/shared/ui/popover';
import './add-edit-address-modal.scss';

type FormValues = {
    address: AddressInput;
    addressType: UserAddressType;
    icon: AddressIconOption;
    name?: string;
};

const ICON_OPTIONS = [
    { label: <AddressIcon icon={AddressIconOption.MAP_PIN} />, value: AddressIconOption.MAP_PIN },
    { label: <AddressIcon icon={AddressIconOption.ORDER} />, value: AddressIconOption.ORDER },
    { label: <AddressIcon icon={AddressIconOption.CART} />, value: AddressIconOption.CART },
    { label: <AddressIcon icon={AddressIconOption.DELIVERY} />, value: AddressIconOption.DELIVERY },
    { label: <AddressIcon icon={AddressIconOption.STAR} />, value: AddressIconOption.STAR },
    { label: <AddressIcon icon={AddressIconOption.DRONE} />, value: AddressIconOption.DRONE },
    { label: <AddressIcon icon={AddressIconOption.CALENDAR} />, value: AddressIconOption.CALENDAR },
    { label: <AddressIcon icon={AddressIconOption.FIRE} />, value: AddressIconOption.FIRE },
    { label: <AddressIcon icon={AddressIconOption.HEART} />, value: AddressIconOption.HEART },
    {
        label: <AddressIcon icon={AddressIconOption.CURRENT_LOCATION} />,
        value: AddressIconOption.CURRENT_LOCATION,
    },
];

const ADDRESS_TYPE_OPTIONS = [UserAddressType.HOME, UserAddressType.WORK, UserAddressType.OTHER];

type Props = ModalProps & {
    id?: string;
    defaultValues?: Partial<FormValues>;
    onSuccess?: (id: string, address: Address) => void;
    children?: ReactNode;
};

type AddEditAddressModalContextValue = {
    id?: string;
    defaultValues?: Partial<FormValues>;
    onSuccess?: (id: string, address: Address) => void;
    onClose: ModalProps['onClose'];
    form: UseFormReturn<FormValues>;
    addressesData?: Awaited<
        ReturnType<NonNullable<ReturnType<typeof QueryApi.Customer.queries.Addresses>['queryFn']>>
    >;
};

const AddEditAddressModalContext = createContext<AddEditAddressModalContextValue | null>(null);

const useAddEditAddressModalContext = () => {
    const context = useContext(AddEditAddressModalContext);
    if (!context) {
        throw new Error('AddEditAddressModal components must be used within AddEditAddressModal.');
    }
    return context;
};

const AddEditAddressModalProvider = ({ id, defaultValues, onSuccess, children, ...props }: Props) => {
    const { data: addressesData } = useQuery(QueryApi.Customer.queries.Addresses({ offset: 0, limit: 100 }));

    const form = useForm<FormValues>({
        defaultValues: {
            address: defaultValues?.address ?? {},
            icon: defaultValues?.icon ?? AddressIconOption.MAP_PIN,
            addressType: defaultValues?.addressType ?? UserAddressType.HOME,
            name: defaultValues?.name ?? '',
        },
    });

    useEffect(() => {
        if (id && !defaultValues) {
            const addressItem = addressesData?.items?.find((a) => a.id === id);

            if (addressItem) {
                form.reset({
                    address: {
                        street: addressItem.address.street,
                        city: addressItem.address.city,
                        postCode: addressItem.address.postCode,
                        country: addressItem.address.country,
                        state:
                            'state' in addressItem.address
                                ? (addressItem.address as { state: string }).state
                                : '',
                        lat: addressItem.address.lat ?? undefined,
                        lon: addressItem.address.lon ?? undefined,
                    },
                    icon: addressItem.icon as AddressIconOption,
                    addressType: UserAddressType.OTHER,
                    name: addressItem.title,
                });
            }
        }
    }, [id, addressesData, form, defaultValues]);

    return (
        <AddEditAddressModalContext.Provider
            value={{
                id,
                defaultValues,
                onSuccess,
                onClose: props.onClose,
                form,
                addressesData,
            }}
        >
            {children}
        </AddEditAddressModalContext.Provider>
    );
};

const AddEditAddressModalTitle = () => {
    const t = useTranslations();
    const { id } = useAddEditAddressModalContext();
    return (
        <Modal.Title
            data-testid="add-edit-address-modal-title"
            className="add-edit-address-modal__title"
            data-mode={id ? 'edit' : 'add'}
        >
            {t(`ADD_EDIT_ADDRESS_MODAL.TITLE_${id ? 'EDIT' : 'ADD'}`)}
        </Modal.Title>
    );
};

type AddEditAddressModalFormProps = ComponentProps<'form'>;
const AddEditAddressModalForm = ({ children, ...props }: AddEditAddressModalFormProps) => {
    const { form } = useAddEditAddressModalContext();
    return (
        <Form {...form} {...props}>
            {children}
        </Form>
    );
};

const AddEditAddressModalAddressField = () => {
    return (
        <Form.AddressAutocompleteField
            name="address"
            rules={{ validate: (value) => !!Object.keys(value).length }}
            data-testid="add-edit-address-modal-address-field"
        />
    );
};

const AddEditAddressModalNameLabel = () => {
    const t = useTranslations();
    return <>{t('ADD_EDIT_ADDRESS_MODAL.NAME_LABEL')}</>;
};

const AddEditAddressModalNameDescription = () => {
    const t = useTranslations();
    return <>{t('ADD_EDIT_ADDRESS_MODAL.NAME_DESCRIPTION')}</>;
};

const AddEditAddressModalIconDropdown = () => {
    const t = useTranslations();
    const { form } = useAddEditAddressModalContext();
    const [icon] = useWatch({
        control: form.control,
        name: ['icon'],
    });

    return (
        <Form.Dropdown
            name="icon"
            triggerContent={
                <AddressIcon
                    icon={icon}
                    className="add-edit-address-modal__icon-placeholder"
                    data-testid="add-edit-address-modal-icon"
                    data-value={icon}
                />
            }
            options={(onChange) => (
                <>
                    <Dropdown.Label data-testid="add-edit-address-modal-icon-dropdown-label">
                        {t('ADD_EDIT_ADDRESS_MODAL.DROPDOWN_LABEL')}
                    </Dropdown.Label>
                    <div className="add-edit-address-modal__icon-dropdown-content-grid">
                        {ICON_OPTIONS.map(({ label, value }) => (
                            <Dropdown.Item
                                key={value}
                                onClick={() => {
                                    onChange(value);
                                }}
                                className={cn(
                                    'add-edit-address-modal__icon-dropdown-item',
                                    value === icon && 'add-edit-address-modal__icon-dropdown-item--selected',
                                )}
                                data-testid={`add-edit-address-modal-icon-option-${value}`}
                                data-value={value}
                            >
                                {label}
                            </Dropdown.Item>
                        ))}
                    </div>
                </>
            )}
            rules={{
                required: true,
            }}
            className="add-edit-address-modal__icon-dropdown-trigger"
            contentProps={{
                className: 'add-edit-address-modal__icon-dropdown-content',
            }}
        />
    );
};

const AddEditAddressModalAddressTypeSelect = () => {
    const t = useTranslations();
    const { form } = useAddEditAddressModalContext();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isAddressTypeOpen, setIsAddressTypeOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
    const [addressType] = useWatch({
        control: form.control,
        name: ['addressType'],
    });

    const handleSelectAddressType = (index?: number) => {
        const nextAddressType = ADDRESS_TYPE_OPTIONS[index ?? focusedIndex ?? 0];
        form.setValue('addressType', nextAddressType, {
            shouldValidate: true,
            shouldDirty: true,
        });
        if (nextAddressType === UserAddressType.OTHER) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
            setIsAddressTypeOpen(false);
        }
    };
    const handleKeydown = (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prev) => {
                return Math.min((prev ?? 0) + 1, ADDRESS_TYPE_OPTIONS.length - 1);
            });
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => {
                return Math.max((prev ?? 0) - 1, 0);
            });
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSelectAddressType();
        }
    };

    return (
        <Popover
            open={isAddressTypeOpen}
            onOpenChange={(openState) => {
                setIsAddressTypeOpen(openState);
                if (!openState) {
                    setFocusedIndex(null);
                }
            }}
        >
            <Popover.Trigger autoFocus={false} asChild>
                <div
                    className={cn(
                        'add-edit-address-modal__address-type-trigger',
                        isAddressTypeOpen && 'add-edit-address-modal__address-type-trigger--open',
                    )}
                    data-testid="add-edit-address-modal-address-type-trigger"
                >
                    {addressType === UserAddressType.OTHER ? (
                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    ref={inputRef}
                                    onKeyDown={handleKeydown}
                                    data-testid="add-edit-address-modal-name-input"
                                    placeholder={t('ADD_EDIT_ADDRESS_MODAL.NAME_PLACEHOLDER')}
                                />
                            )}
                        />
                    ) : (
                        <span
                            data-testid="add-edit-address-modal-address-type-display"
                            data-value={addressType}
                        >
                            {t(`ADDRESS.TYPE.${addressType}`)}
                        </span>
                    )}
                    <Icon name="chevron-right" />
                </div>
            </Popover.Trigger>
            <Popover.Content
                className={cn(
                    'add-edit-address-modal__address-type-content',
                    isAddressTypeOpen && 'add-edit-address-modal__address-type-content--open',
                )}
                sideOffset={0}
                autoFocus={false}
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                }}
                onKeyDown={handleKeydown}
                avoidCollisions={false}
                onMouseLeave={() => {
                    setFocusedIndex(null);
                }}
            >
                {ADDRESS_TYPE_OPTIONS.map((type, index) => (
                    <button
                        className={cn(
                            'add-edit-address-modal__address-type-item',
                            focusedIndex === index && 'add-edit-address-modal__address-type-item--focused',
                            addressType === type && 'add-edit-address-modal__address-type-item--selected',
                        )}
                        key={type}
                        type="button"
                        onMouseEnter={() => {
                            setFocusedIndex(index);
                        }}
                        onClick={() => {
                            handleSelectAddressType(index);
                        }}
                        data-testid={`add-edit-address-modal-address-type-${type.toLowerCase()}`}
                        data-value={type}
                    >
                        {t(`ADDRESS.TYPE.${type}`)}
                    </button>
                ))}
            </Popover.Content>
        </Popover>
    );
};

const AddEditAddressModalSubmitButton = () => {
    const t = useTranslations();
    const queryClient = useQueryClient();
    const { id, form, addressesData, onSuccess, onClose } = useAddEditAddressModalContext();

    const createAddressMutation = useMutation(QueryApi.Customer.mutations.CreateAddress);
    const updateAddressMutation = useMutation(QueryApi.Customer.mutations.UpdateAddress);

    const handleSubmit = async (data: FormValues) => {
        const title =
            data.addressType === UserAddressType.OTHER
                ? data.name || ''
                : t(`ADDRESS.TYPE.${data.addressType}`);

        const currentAddress = id ? addressesData?.items?.find((a) => a.id === id) : null;

        const payload = {
            title,
            address: {
                street: data.address.street,
                city: data.address.city,
                postCode: data.address.postCode,
                state: data.address.state || '',
                country: data.address.country,
                lat: data.address.lat || '0',
                lon: data.address.lon || '0',
                timezone: null,
            },
            icon: data.icon,
            isDefault: !id || (currentAddress?.isDefault ?? false),
        };

        if (id) {
            await updateAddressMutation.mutateAsync({
                addressId: id,
                ...payload,
            });
            onSuccess?.(id, payload.address as unknown as Address);
        } else {
            const newApiAddress = await createAddressMutation.mutateAsync(payload);
            onSuccess?.(newApiAddress.id, newApiAddress.address as unknown as Address);
        }

        queryClient.invalidateQueries({ queryKey: ['CUSTOMER_ADDRESSES'] });

        onClose();
    };

    return (
        <Button
            className="add-edit-address-modal__submit-button"
            onClick={form.handleSubmit(handleSubmit)}
            data-testid="add-edit-address-modal-submit-button"
            disabled={createAddressMutation.isPending || updateAddressMutation.isPending}
        >
            <span
                data-testid="add-edit-address-modal-submit-button-text"
                data-action={id ? 'save' : 'continue'}
            >
                {t('WORDS.SAVE_ADDRESS')}
            </span>
        </Button>
    );
};

const AddEditAddressModalDeleteButton = () => {
    const { id, form, onClose } = useAddEditAddressModalContext();
    const deleteAddressMutation = useMutation(QueryApi.Customer.mutations.DeleteAddress);

    const handleDeleteClick = () => {
        ModalService.show(ConfirmDeleteAddressModal, {
            addressName: form.getValues('name')!,
            onConfirm: () => {
                onClose();
                void deleteAddressMutation.mutateAsync({ addressId: id! });
            },
        });
    };

    if (!id) {
        return null;
    }

    return (
        <Button
            variant="ghost"
            size="icon"
            className="add-edit-address-modal__delete-button"
            onClick={handleDeleteClick}
            data-testid="add-edit-address-modal-delete-button"
            disabled={deleteAddressMutation.isPending}
        >
            <Icon name="trash" />
        </Button>
    );
};

export const AddEditAddressModalWrapper = ({ children, ...props }: Props) => {
    return (
        <AddEditAddressModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'add-edit-address-modal',
                    containerProps: {
                        className: 'add-edit-address-modal__container',
                    },
                    'data-testid': 'add-edit-address-modal',
                }}
            >
                {children}
            </Modal>
        </AddEditAddressModalProvider>
    );
};

export {
    AddEditAddressModalWrapper as Wrapper,
    AddEditAddressModalProvider as Provider,
    AddEditAddressModalTitle as Title,
    AddEditAddressModalForm as Form,
    AddEditAddressModalAddressField as AddressField,
    AddEditAddressModalNameLabel as NameLabel,
    AddEditAddressModalNameDescription as NameDescription,
    AddEditAddressModalIconDropdown as IconDropdown,
    AddEditAddressModalAddressTypeSelect as AddressTypeSelect,
    AddEditAddressModalSubmitButton as SubmitButton,
    AddEditAddressModalDeleteButton as DeleteButton,
};
